import { getLocalizedString } from "common/components/localization";
import { File } from "common/react-plucked";
import { isEmptyOrUndefined } from "common/Support";
import OverlayEditor from "../../deprecated/OverlayEditor";
import type { HasOverlayProps, HasOverlayState } from "../../smart/has-overlay-component/has-overlay-component";
import HasOverlayComponent from "../../smart/has-overlay-component/has-overlay-component";
import { NotePreviewStyle } from "./note-preview-style";

interface Props extends HasOverlayProps {
    readonly isEnabled: boolean;
    readonly isRequired: boolean;

    readonly note?: string;
    readonly placeholder?: string;
    readonly title?: string;
    readonly className?: string;

    readonly onSave?: (text: string) => void;
    readonly onTap?: () => void;
}

interface State extends HasOverlayState {}

class NotePreviewImpl extends HasOverlayComponent<Props, State> {
    public state: State = {};

    private onPreviewClicked = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (this.props.onTap !== undefined) {
            this.props.onTap();
        } else {
            this.showOverlay();
        }
    };

    private onEditorClosed = () => {
        this.hideOverlay();
    };

    private onTextSaved = (text: string) => {
        if (this.props.onSave !== undefined) {
            this.hideOverlay();
            this.props.onSave(text);
        }
    };

    public shouldComponentUpdate(nextProps: Props, nextState: State, _nextContext: any): boolean {
        const { title, placeholder, note, isEnabled } = this.props;

        return (
            nextProps.note !== note ||
            nextProps.placeholder !== placeholder ||
            nextState.overlayState !== this.state.overlayState ||
            nextProps.title !== title ||
            nextProps.isEnabled !== isEnabled
        );
    }

    public render(): React.ReactNode {
        const { className, placeholder, renderEnvironment, note, isEnabled, isRequired } = this.props;
        const { appKind } = renderEnvironment.appEnvironment;

        let body: React.ReactNode;

        if (note === undefined || note.length === 0) {
            const text = isRequired
                ? getLocalizedString("required", appKind)
                : getLocalizedString("addANewNote", appKind);
            body = (
                <div className="add-label">
                    <File size={24} />
                    <span>{text}</span>
                </div>
            );
        } else {
            body = (
                <>
                    <div className="body-wrapper">{note}</div>
                    <button
                        className="add-label"
                        onClick={this.onPreviewClicked}
                        aria-label={getLocalizedString("editNote", appKind)}>
                        {getLocalizedString("editNote", appKind)}
                    </button>
                </>
            );
        }

        let holder = placeholder;
        if (holder === undefined || holder.length === 0) {
            holder = getLocalizedString("writeANote", appKind);
        }

        let title = this.props.title;
        if (isEmptyOrUndefined(title)) {
            title = getLocalizedString("notes", appKind);
        }

        let portal: React.ReactNode;
        if (this.shouldRenderPortal()) {
            portal = this.getPortal(
                <OverlayEditor
                    renderEnvironment={renderEnvironment}
                    title={title}
                    placeholder={holder}
                    text={note}
                    saved={this.onTextSaved}
                    saveText={getLocalizedString("done", appKind)}
                    saveIcon={"check"}
                    closed={this.onEditorClosed}
                    closedText={getLocalizedString("cancel", appKind)}
                    allowEmpty={true}
                />
            );
        }

        const labelFor = `notes-p-${title}`;

        return (
            <>
                <NotePreviewStyle
                    className={className}
                    data-test="app-note-preview"
                    isEnabled={isEnabled}
                    onClick={this.onPreviewClicked}>
                    {title !== undefined && title.length > 0 && (
                        <label htmlFor={labelFor} className="header">
                            {title}
                        </label>
                    )}

                    <div id={labelFor} className="note-body-area">
                        {body}
                    </div>
                </NotePreviewStyle>
                {portal}
            </>
        );
    }
}

const NotePreview = NotePreviewImpl;
export default NotePreview;
